import axios from 'axios';

/**
 * Interceptor for successful responses (2xx).
 */
export function AxiosResponseInterceptor(response) {
  return response;
}

/**
 * Interceptor for error responses.
 * If the server returns 500 and we haven't retried yet, we:
 *  1) Refresh the token (using refreshToken from cookies).
 *  2) Update the auth header.
 *  3) Retry the original request once.
 * If the refresh fails or if we've already retried, we reject the error.
 */
export async function AxiosResponseErrorInterceptor(error) {
  if (
    error.response &&
    error.response.status === 500 &&
    !error.config._retry // <== We check a custom flag to avoid infinite loops
  ) {
    console.warn('HTTP 500: Attempting token refresh and one retry...');

    // Mark this request so we don't retry again
    error.config._retry = true;

    const cookies = getCookies();
    const refreshToken = cookies['refreshToken'];

    if (!refreshToken) {
      console.error('No refresh token found. Cannot attempt refresh.');
      return Promise.reject(error);
    }

    try {
      // Attempt to refresh
      document.cookie=`accessToken=; path=/`;
      const tokenResponse = await axios.get('/sc/api/auth/refreshToken?refreshToken='+refreshToken);
      const newAccessToken = tokenResponse.data.content.accessToken;

      // Update cookies with the new token
      document.cookie = `accessToken=${newAccessToken}; path=/`;
      document.cookie = `refreshToken=${refreshToken}; path=/`;

      // Re-attach the new token in the header
      error.config.headers.Authorization = `Bearer ${newAccessToken}`;

      // **Retry** the original request
      return axios(error.config);

    } catch (refreshError) {
      console.error('Failed to refresh token:', refreshError);
      window.location.href = '/login';

      // If refresh fails, we DO NOT log out automatically in this scenario.
      // We simply reject so the calling code can handle it or show an error.
      return Promise.reject(refreshError);
    }
  } else {
    // For all other errors (or if we've already retried), reject.
    console.error('Error response:', error.response?.data || error.message);
    return Promise.reject(error);
  }
}

/**
 * Parse document.cookie into a key-value object.
 */
function getCookies() {
  return document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.split('=').map((item) => item.trim());
    acc[key] = value;
    return acc;
  }, {});
}

/**
 * If you do need a function to clear cookies:
 */
function clearCookies() {
  document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
  document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
}